import { Observable, of, EMPTY, merge } from 'rxjs';
import { map, switchMap, distinctUntilChanged, withLatestFrom, debounceTime } from 'rxjs/operators';

import { CookieBannerEvents } from '../actions';
import { CookieBannerStore } from '../types';

interface CustomService {
    name: string;
    cookieNames: string[];
    description?: string;
}

/**
 * An array of the custom services we want to detect and how to identify them.
 */
const CUSTOM_SERVICES: CustomService[] = [
    {
        name: 'algolia_analytics',
        cookieNames: ['_ALGOLIA'],
        description: 'Algolia Search & Analytics',
    },
];

/**
 * Detect custom defined services based on accepted cookies.
 *
 * CookieFirst can identify a number of services based on the cookies accepted,
 * however it doesn't detect all the services we use. This epic is an
 * additional check to help us identify services that are not detected
 * by CookieFirst.
 */
export function customServicesEpic(_action$: Observable<any>, state$: Observable<CookieBannerStore>): Observable<any> {
    const ready$ = state$.pipe(
        map((state) => state.cookieBanner?.ready),
        distinctUntilChanged(),
    );

    const consent$ = state$.pipe(
        map((state) => state.cookieBanner?.consented),
        distinctUntilChanged(),
    );

    /**
     * When the Cookie Banner is ready or consent changes,
     * check for custom services based on the cookie scan data.
     */
    return merge(ready$, consent$).pipe(
        debounceTime(50),
        withLatestFrom(state$),
        switchMap(([_, state]) => {
            const cookieBanner = state.cookieBanner;
            const scanData = cookieBanner?.cookieScan;
            const categories = cookieBanner?.categories || {};

            if (!scanData?.cookies?.length) {
                console.warn('Cookie scan data not available for custom services detection');

                return EMPTY;
            }

            const serviceUpdates = CUSTOM_SERVICES.map((service) => {
                const serviceCookies = scanData.cookies.filter((cookie) =>
                    service.cookieNames.some((name) => cookie.name?.toLowerCase().includes(name.toLowerCase())),
                );

                if (!serviceCookies.length) {
                    return null;
                }

                const cookieCategories = [...new Set(serviceCookies.map((cookie) => cookie.category).filter(Boolean))];

                const isAllowed = cookieCategories.every((category) => {
                    if (!category || !categories[category]) {
                        return false;
                    }

                    return categories[category] === true;
                });

                return {
                    name: service.name,
                    description: service.description,
                    enabled: isAllowed,
                };
            }).filter(Boolean);

            if (serviceUpdates.length) {
                return of({
                    type: CookieBannerEvents.CUSTOM_SERVICES_DETECTED,
                    payload: serviceUpdates,
                });
            }

            return EMPTY;
        }),
    );
}

export default customServicesEpic;
